import type {
  CategoryBase,
  PresetListSection,
  PresetListSectionItem,
  PresetSection,
  PresetSectionItem,
  Structure,
} from '@wix/add-panel-common';
import type { MouseEvent, MouseEventHandler } from 'react';
import { useActionsDispatcher } from '../../../../../ActionsDispatcher';
import { usePreventDoubleClick } from '../../../../../hooks/usePreventDoubleClick';

interface UseHandleClick {
  handleClick: MouseEventHandler;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

interface UseHandleClickProps {
  category: CategoryBase;
  section: PresetSection | PresetListSection;
  item: PresetSectionItem | PresetListSectionItem;
  sectionIndex?: number;
  itemIndex?: number;
}

export const useHandleMouseEvents = ({
  category,
  section,
  item,
  sectionIndex,
  itemIndex,
}: UseHandleClickProps): UseHandleClick => {
  const { dispatchComponentClick, dispatchComponentMouseEnter, dispatchComponentMouseLeave } = useActionsDispatcher();

  const { isAnotherMouseActionAllowed, setClickTimeStamp } = usePreventDoubleClick();

  const extendCompStructure = (structure: Structure): Structure => {
    if (typeof structure.style === 'string') {
      return structure;
    }

    const style = structure.styleId || (structure.style.styleType === 'system' ? structure.style.id : structure.style);
    return { ...structure, style };
  };

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();

    if (isAnotherMouseActionAllowed(event)) {
      const { clientX, clientY } = event;

      dispatchComponentClick({
        category,
        section,
        customClickAndDropHandler: 'customClickAndDropHandler' in item ? item.customClickAndDropHandler : undefined,
        item,
        eventOrigin: { clientX, clientY },
        compDef: extendCompStructure(item.structure),
      });
    }
    setClickTimeStamp(event);
  };

  const handleMouseEnter = () => {
    dispatchComponentMouseEnter({
      itemId: item.id,
      compDef: item.structure,
      metaData: {
        sectionName: section.sectionName,
        sectionIndex,
        itemIndex,
        categoryId: category.id,
      },
    });
  };

  const handleMouseLeave = () => {
    dispatchComponentMouseLeave({ itemId: item.id });
  };

  return { handleClick, handleMouseEnter, handleMouseLeave };
};
